import { useEffect, useLayoutEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import clsx from 'clsx';
import { setWasserijItemKorting } from '../../lib/orders';

interface ItemKortingControlProps {
  itemId: string;
  /** Actieve handmatige korting (%) op deze regel, of null als er geen is. */
  currentPct: number | null;
  /** Tekst voor de inactieve trigger-link (default "Korting"). */
  label?: string;
  onSaved: (nieuweLastModifiedDate: string | null) => void;
}

/** Korting-snelkeuzes: gratis weggeven of standaard-afprijzingen. 100 = gratis. */
const PRESETS: { pct: number; label: string }[] = [
  { pct: 100, label: 'Gratis' },
  { pct: 50, label: '50%' },
  { pct: 25, label: '25%' },
];

/** Breedte van de popover (px) — gebruikt om hem binnen het scherm te ankeren. */
const POPOVER_WIDTH = 224;

function formatPct(pct: number): string {
  return Number.isInteger(pct) ? String(pct) : pct.toFixed(2).replace(/\.?0+$/, '');
}

/**
 * Inline pill + popover om per wasserij-item-regel (kleed of behandeling) een
 * handmatige korting te zetten — gratis (100%) of een percentage. Schrijft via
 * `setWasserijItemKorting` (Korting__c + Andere_prijs__c=true zodat de order-
 * cascade het niet overschrijft). De order-korting in de header blijft los.
 *
 * De popover rendert via een portal met `position: fixed`, omdat de item-groep
 * een `overflow-hidden` container is die een gewone absolute popover zou clippen.
 */
export function ItemKortingControl({
  itemId,
  currentPct,
  label = 'Korting',
  onSaved,
}: ItemKortingControlProps) {
  const [open, setOpen] = useState(false);
  const [input, setInput] = useState('');
  const [pending, setPending] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [coords, setCoords] = useState<{ top: number; left: number } | null>(null);
  const triggerRef = useRef<HTMLButtonElement | null>(null);
  const popoverRef = useRef<HTMLDivElement | null>(null);
  const inputRef = useRef<HTMLInputElement | null>(null);

  const active = currentPct != null && currentPct > 0;
  const pct = currentPct ?? 0;

  // Anker de popover onder de trigger, rechts uitgelijnd, binnen het scherm.
  useLayoutEffect(() => {
    if (!open || !triggerRef.current) return;
    const r = triggerRef.current.getBoundingClientRect();
    const left = Math.max(8, Math.min(r.right - POPOVER_WIDTH, window.innerWidth - POPOVER_WIDTH - 8));
    setCoords({ top: r.bottom + 4, left });
  }, [open]);

  useEffect(() => {
    if (!open) return;
    setInput(active ? formatPct(pct) : '');
    setError(null);
    const t = window.setTimeout(() => inputRef.current?.focus(), 30);
    return () => window.clearTimeout(t);
  }, [open, active, pct]);

  useEffect(() => {
    if (!open) return;
    const onPointer = (e: MouseEvent) => {
      const t = e.target as Node;
      if (triggerRef.current?.contains(t) || popoverRef.current?.contains(t)) return;
      setOpen(false);
    };
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setOpen(false);
    };
    // Bij scrollen/resizen klopt de vaste positie niet meer — sluit dan gewoon.
    const onReflow = () => setOpen(false);
    document.addEventListener('mousedown', onPointer);
    document.addEventListener('keydown', onKey);
    window.addEventListener('scroll', onReflow, true);
    window.addEventListener('resize', onReflow);
    return () => {
      document.removeEventListener('mousedown', onPointer);
      document.removeEventListener('keydown', onKey);
      window.removeEventListener('scroll', onReflow, true);
      window.removeEventListener('resize', onReflow);
    };
  }, [open]);

  const save = async (next: number | null) => {
    if (pending) return;
    setPending(true);
    setError(null);
    try {
      const result = await setWasserijItemKorting(itemId, next);
      onSaved(result.nieuweLastModifiedDate);
      setOpen(false);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Opslaan mislukt');
    } finally {
      setPending(false);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    const trimmed = input.trim().replace(',', '.');
    if (!trimmed) {
      await save(null);
      return;
    }
    const parsed = Number.parseFloat(trimmed);
    if (!Number.isFinite(parsed) || parsed < 0 || parsed > 100) {
      setError('Geef een percentage tussen 0 en 100');
      return;
    }
    await save(parsed > 0 ? parsed : null);
  };

  const pillClass = clsx(
    'inline-flex items-center gap-1 text-[11px] px-1.5 py-0.5 rounded font-medium border transition-colors',
    active
      ? 'bg-emerald-50 text-emerald-700 border-emerald-200 hover:ring-1 hover:ring-emerald-300'
      : 'bg-transparent text-slate-400 border-transparent hover:text-indigo-600 hover:underline',
    pending && 'opacity-60'
  );

  const triggerLabel = active ? (pct === 100 ? 'Gratis' : `−${formatPct(pct)}%`) : label;

  return (
    <>
      <button
        ref={triggerRef}
        type="button"
        onClick={() => setOpen((o) => !o)}
        disabled={pending}
        className={pillClass}
        aria-haspopup="dialog"
        aria-expanded={open}
      >
        {pending && (
          <svg className="w-3 h-3 animate-spin" fill="none" viewBox="0 0 24 24">
            <circle cx="12" cy="12" r="10" stroke="currentColor" strokeOpacity="0.25" strokeWidth="4" />
            <path d="M4 12a8 8 0 018-8" stroke="currentColor" strokeWidth="4" />
          </svg>
        )}
        <span>{triggerLabel}</span>
      </button>

      {open && coords
        ? createPortal(
            <div
              ref={popoverRef}
              role="dialog"
              style={{ position: 'fixed', top: coords.top, left: coords.left, width: POPOVER_WIDTH }}
              className="z-50 bg-white border border-slate-200 rounded-lg shadow-lg p-3"
            >
              <div className="flex flex-wrap gap-1.5 mb-2">
                {PRESETS.map((p) => (
                  <button
                    key={p.pct}
                    type="button"
                    onClick={() => save(p.pct)}
                    disabled={pending}
                    className={clsx(
                      'text-xs px-2 py-1 rounded border font-medium disabled:opacity-60',
                      pct === p.pct
                        ? 'bg-emerald-600 text-white border-emerald-600'
                        : 'bg-white text-slate-700 border-slate-300 hover:border-emerald-400 hover:text-emerald-700'
                    )}
                  >
                    {p.label}
                  </button>
                ))}
              </div>
              <form onSubmit={handleSubmit} className="flex flex-col gap-2">
                <label className="text-[11px] font-medium text-slate-600">Anders (%)</label>
                <div className="flex items-center gap-2">
                  <input
                    ref={inputRef}
                    type="text"
                    inputMode="decimal"
                    value={input}
                    onChange={(e) => setInput(e.target.value)}
                    placeholder="0"
                    disabled={pending}
                    className="flex-1 border border-slate-300 rounded px-2 py-1 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500"
                  />
                  <span className="text-sm text-slate-500">%</span>
                </div>
                <div className="flex items-center gap-2">
                  <button
                    type="submit"
                    disabled={pending}
                    className="flex-1 bg-indigo-600 hover:bg-indigo-700 text-white text-sm font-medium px-3 py-1.5 rounded disabled:opacity-60"
                  >
                    Opslaan
                  </button>
                  {active && (
                    <button
                      type="button"
                      onClick={() => save(null)}
                      disabled={pending}
                      className="text-xs text-red-600 hover:text-red-700 disabled:opacity-60"
                    >
                      Verwijderen
                    </button>
                  )}
                </div>
                {error && <p className="text-xs text-amber-700">⚠ {error}</p>}
                <p className="text-[11px] text-slate-400">
                  Geldt alleen voor deze regel. Order-korting blijft apart.
                </p>
              </form>
            </div>,
            document.body
          )
        : null}
    </>
  );
}
